// src/components/Screens/MarketsChecklistPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVault } from "../../contexts/VaultContext";

const FILE_CHECKLISTS = "markets/checklists.json";
const FILE_TEMPLATES  = "markets/templates.json";

const DEFAULT = { lists: [], updatedAt: null };
const DEFAULT_TEMPLATES = {
  free: [
    { id: "tpl_basic_market", name: "Basic Market Day", items: [
      "Float / change","Square/Stripe reader charged","Table cloths","Price tags / signage","Packaging: paper bags, tissue, bubble wrap","Business cards","Stock list & inventory sheet","Hand sanitiser & wipes","Water & snacks","Shade/gazebo + weights","Extension lead & power board (if needed)",
    ]},
    { id: "tpl_ceramics_booth", name: "Ceramics Booth", items: [
      "Display risers / crates","Props: flowers / greenery","Glaze care card holder","QR code sign for Instagram","Spare price stickers & marker","Small broom & cloth","Emergency kit (tape, scissors, zip ties)","Spare light bulbs (if night)",
    ]},
  ],
  // You can later sell/provide downloadable packs and drop them here:
  pro: [
    // Example (locked showcase)
    // { id: "tpl_premium_all_weather", name: "All-Weather Pack (Pro)", items: [...], locked: true, price: "A$6.99" }
  ],
};

function useVaultJson(key, fallback) {
  const { ready, getJSON, setJSON } = useVault();
  const [value, setValue] = useState(fallback);
  
  // Load from vault
  useEffect(() => {
    let active = true;
    if (!ready) return;
    (async () => {
      const v = await getJSON(key, fallback);
      if (active) setValue(v ?? fallback);
    })();
    return () => { active = false; };
  }, [ready, key, getJSON]);

  // Save to vault with debounce to reduce calls and ensure Drive persistence
  useEffect(() => {
    if (!ready || !value) return;
    const timer = setTimeout(() => {
      setJSON(key, value);
    }, 500);
    return () => clearTimeout(timer);
  }, [ready, key, value, setJSON]);
  
  return [value, setValue];
}

export default function MarketsChecklistPage({ isDark }) {
  const nav = useNavigate();
  const { setJSON: vaultSetJSON } = useVault();
  const [data, setData] = useVaultJson(FILE_CHECKLISTS, DEFAULT);
  const [templates, setTemplates] = useVaultJson(FILE_TEMPLATES, DEFAULT_TEMPLATES);

  // Ensure at least one list
  useEffect(() => {
    if (!Array.isArray(data?.lists) || data.lists.length === 0) {
      const seed = { id: `list_${Date.now()}`, title: "Market Day", items: [] };
      setData({ lists: [seed], updatedAt: new Date().toISOString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeId, setActiveId] = useState(null);
  const activeList = useMemo(() => {
    if (!data?.lists?.length) return null;
    const id = activeId || data.lists[0].id;
    return data.lists.find((l) => l.id === id) || data.lists[0];
  }, [data, activeId]);

  const isLocked = (tpl) => !!tpl.locked;

  const saveLists = async (lists) => {
    const nextData = { lists, updatedAt: new Date().toISOString() };
    setData(nextData);
    // Force immediate save to Drive
    try {
      await vaultSetJSON(FILE_CHECKLISTS, nextData);
    } catch (err) {
      console.error("Failed to save checklists to Drive:", err);
    }
  };

  const addList = () => {
    const title = prompt("New checklist name", "New Market Checklist");
    if (!title) return;
    const next = { id: `list_${Date.now().toString(36)}`, title: title.trim(), items: [] };
    saveLists([next, ...(data.lists || [])]);
    setActiveId(next.id);
  };

  const renameList = (list) => {
    const title = prompt("Rename checklist", list.title || "Checklist");
    if (!title) return;
    const next = (data.lists || []).map((l) => l.id === list.id ? { ...l, title: title.trim() } : l);
    saveLists(next);
  };

  const deleteList = (list) => {
    if (!window.confirm(`Delete "${list.title}"?`)) return;
    const next = (data.lists || []).filter((l) => l.id !== list.id);
    saveLists(next);
    setActiveId(next[0]?.id || null);
  };

  const addItem = () => {
    const txt = prompt("Add task/item");
    if (!txt) return;
    const entry = { id: `it_${Math.random().toString(36).slice(2,8)}`, text: txt.trim(), done: false };
    const next = (data.lists || []).map((l) => l.id === activeList.id ? { ...l, items: [entry, ...(l.items || [])] } : l);
    saveLists(next);
  };

  const toggle = (id) => {
    const next = data.lists.map((l) => {
      if (l.id !== activeList.id) return l;
      return { ...l, items: (l.items || []).map((it) => it.id === id ? { ...it, done: !it.done } : it) };
    });
    saveLists(next);
  };

  const removeItem = (id) => {
    const next = data.lists.map((l) => {
      if (l.id !== activeList.id) return l;
      return { ...l, items: (l.items || []).filter((it) => it.id !== id) };
    });
    saveLists(next);
  };

  const clearDone = () => {
    const next = data.lists.map((l) => {
      if (l.id !== activeList.id) return l;
      return { ...l, items: (l.items || []).filter((it) => !it.done) };
    });
    saveLists(next);
  };

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    if (isLocked(tpl)) {
      alert("This template is part of a Pro pack. (Hook your in-app purchase flow here.)");
      return;
    }
    const seeded = (tpl.items || []).map((t) => ({ id: `it_${Math.random().toString(36).slice(2,8)}`, text: t, done: false }));
    const next = data.lists.map((l) => l.id === activeList.id ? { ...l, items: [...seeded, ...(l.items || [])] } : l);
    saveLists(next);
  };

  const exportList = () => {
    const blob = new Blob([JSON.stringify(activeList, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${activeList.title || "checklist"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importList = async (file) => {
    const txt = await file.text();
    const json = JSON.parse(txt);
    if (!json || !Array.isArray(json.items)) return alert("Invalid checklist file.");
    const imp = { ...json, id: `list_${Date.now().toString(36)}` };
    saveLists([imp, ...(data.lists || [])]);
    setActiveId(imp.id);
  };

  const ring = isDark ? "1px solid rgba(255,255,255,.12)" : "1px solid rgba(0,0,0,.08)";
  const panel = isDark ? "rgba(255,255,255,.06)" : "rgba(255,255,255,.65)";
  const card  = isDark ? "rgba(0,0,0,.25)" : "rgba(255,255,255,.9)";
  const textColor = isDark ? "#ffffff" : "#000000";

  return (
    <div 
      className="max-w-6xl mx-auto space-y-4 min-h-screen p-6" 
      style={{ backgroundColor: 'var(--color-app-bg)', color: textColor }}
    >
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Markets Checklists</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} className="px-3 py-1 rounded text-sm" style={{ border: ring }}>Back</button>
        </div>
      </div>

      {/* Lists + actions */}
      <div className="space-y-3">
        {/* List tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {(data.lists || []).map((l) => (
            <button
              key={l.id}
              onClick={() => setActiveId(l.id)}
              className="px-4 py-2 rounded-lg text-sm transition-all"
              style={{
                border: ring,
                background: activeList?.id === l.id ? "var(--mudo-primary, #f59e0b)" : panel,
                color: activeList?.id === l.id ? "#000" : textColor,
                fontWeight: activeList?.id === l.id ? "600" : "400",
                boxShadow: activeList?.id === l.id ? "0 2px 8px rgba(245, 158, 11, 0.3)" : "none"
              }}
            >
              {l.title || "Checklist"}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={addList} className="px-3 py-1.5 rounded text-sm" style={{ border: ring, background: panel }}>
            + New List
          </button>
          {activeList && (
            <>
              <button onClick={() => renameList(activeList)} className="px-3 py-1.5 rounded text-sm" style={{ border: ring, background: panel }}>
                Rename
              </button>
              <button onClick={() => deleteList(activeList)} className="px-3 py-1.5 rounded text-sm" style={{ border: ring, background: panel, color: "#ef4444" }}>
                Delete
              </button>
              <button onClick={exportList} className="px-3 py-1.5 rounded text-sm" style={{ border: ring, background: panel }}>
                Export
              </button>
              <label className="px-3 py-1.5 rounded text-sm cursor-pointer" style={{ border: ring, background: panel }}>
                Import
                <input type="file" accept="application/json" className="hidden" onChange={(e) => e.target.files?.[0] && importList(e.target.files[0])} />
              </label>
            </>
          )}
        </div>
      </div>

      {/* Active list editor */}
      {activeList && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 rounded-xl p-3 space-y-2" style={{ border: ring, background: card }}>
            <div className="flex items-center justify-between">
              <div className="font-semibold">{activeList.title}</div>
              <div className="flex items-center gap-2">
                <button onClick={addItem} className="px-3 py-1 rounded text-sm" style={{ border: ring }}>+ Add item</button>
                <button onClick={clearDone} className="px-3 py-1 rounded text-sm" style={{ border: ring }}>Clear done</button>
              </div>
            </div>

            {(activeList.items || []).length === 0 ? (
              <div className="text-sm opacity-70">No items yet — add one or use a template.</div>
            ) : (
              <div className="space-y-1">
                {activeList.items.map((it) => (
                  <label key={it.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5">
                    <input type="checkbox" checked={!!it.done} onChange={() => toggle(it.id)} />
                    <input
                      className={`flex-1 bg-transparent outline-none text-sm ${it.done ? "line-through opacity-60" : ""}`}
                      style={{ color: textColor }}
                      defaultValue={it.text}
                      onBlur={(e) => {
                        const txt = e.target.value.trim();
                        const next = (data.lists || []).map((l) => {
                          if (l.id !== activeList.id) return l;
                          return {
                            ...l,
                            items: (l.items || []).map((x) => x.id === it.id ? { ...x, text: txt } : x),
                          };
                        });
                        saveLists(next);
                      }}
                    />
                    <button onClick={() => removeItem(it.id)} className="text-xs opacity-70 hover:opacity-100">Remove</button>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Templates sidebar */}
          <div className="rounded-xl p-3 space-y-3" style={{ border: ring, background: card }}>
            <div className="font-semibold">Templates</div>

            <div>
              <div className="text-xs uppercase opacity-70 mb-1">Free</div>
              <div className="space-y-2">
                {(templates.free || []).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className="w-full text-left px-3 py-2 rounded"
                    style={{ border: ring }}
                    title={`Apply ${t.name}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase opacity-70 mt-3 mb-1">Pro Packs</div>
              {(templates.pro || []).length === 0 ? (
                <div className="text-sm opacity-70">
                  No Pro packs installed yet.
                  <div className="mt-1">
                    <button
                      className="px-3 py-1 rounded text-xs"
                      style={{ border: ring }}
                      onClick={() => alert("Hook this to your in-app shop or Stripe checkout. After purchase, add templates into Vault at markets/templates.json under `pro`.")}
                    >
                      Open Shop
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.pro.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2 rounded"
                      style={{ border: ring, opacity: t.locked ? 0.6 : 1 }}
                      title={t.locked ? `${t.name} (Locked)` : `Apply ${t.name}`}
                    >
                      {t.name}{t.locked ? " — Locked" : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="text-xs opacity-70">
              Tip: you can package & sell curated checklists as **Pro packs**. After purchase, write them into the Vault at <code>markets/templates.json</code> under <code>pro</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
